import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { extractBaseLanguage } from "../u2000Core/tools/U2000LanguageUtils";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import type { U2000AgingBucket } from "../u2000Core/constants/U2000AgingBuckets";
import { NS } from "./U3125";
import {
  doGetArAgingCustomerDetail,
  doGetArAgingReport,
  luHandleAppiError,
  parseU3125ArAgingCustomerDetailResponse,
  parseU3125ArAgingReportResponse,
  u3125ArAgingCacheKey,
  u3125BuildArAgingCustomerDetailRequestBody,
  u3125BuildArAgingRequestBody,
  u3125ExtractCustomerDetailFromReport,
  type U3125ArAgingCustomerDetailReport,
  type U3125ArAgingReport,
} from "./U3125Service";

function isAxiosNotFound(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error != null &&
    "response" in error &&
    (error as { response?: { status?: number } }).response?.status === 404
  );
}

const detailCache = new Map<string, U3125ArAgingCustomerDetailReport>();
const fullReportFallbackCache = new Map<string, U3125ArAgingReport>();

export function u3125ClearArAgingCustomerDetailCache(): void {
  detailCache.clear();
  fullReportFallbackCache.clear();
}

export function useU3125ArAgingCustomerDetailHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [customerDetail, setCustomerDetail] = useState<U3125ArAgingCustomerDetailReport | null>(null);
  const [loadingCustomerId, setLoadingCustomerId] = useState<string | null>(null);
  const lastRequestKeyRef = useRef<string>("");

  const loadCustomerDetail = useCallback(
    async (
      asOfDate: string,
      customerId: string,
      bucket?: U2000AgingBucket | null,
    ): Promise<U3125ArAgingCustomerDetailReport | null> => {
      const trimmedDate = asOfDate?.trim().slice(0, 10);
      const trimmedCustomerId = customerId?.trim();
      if (!trimmedDate || trimmedDate.length < 10 || !trimmedCustomerId) return null;

      const cacheKey = u3125ArAgingCacheKey(trimmedDate, bucket, trimmedCustomerId);
      const cached = detailCache.get(cacheKey);
      if (cached) {
        setCustomerDetail(cached);
        setLoadingCustomerId(null);
        return cached;
      }

      const requestKey = cacheKey;
      lastRequestKeyRef.current = requestKey;
      setLoadingCustomerId(trimmedCustomerId);
      try {
        const lang = extractBaseLanguage(i18n.language);
        const body = u3125BuildArAgingCustomerDetailRequestBody({
          asOfDate: trimmedDate,
          customerId: trimmedCustomerId,
          lang,
          bucket,
        });
        let parsed: U3125ArAgingCustomerDetailReport | null = null;
        try {
          const res = await doGetArAgingCustomerDetail(axios, body);
          parsed = parseU3125ArAgingCustomerDetailResponse(res.data);
        } catch (error: unknown) {
          if (!isAxiosNotFound(error)) throw error;
          const fallbackKey = u3125ArAgingCacheKey(trimmedDate, bucket);
          let fullReport = fullReportFallbackCache.get(fallbackKey);
          if (!fullReport) {
            const reportBody = u3125BuildArAgingRequestBody({ asOfDate: trimmedDate, lang, bucket });
            const res = await doGetArAgingReport(axios, reportBody);
            fullReport = parseU3125ArAgingReportResponse(res.data) ?? undefined;
            if (fullReport) fullReportFallbackCache.set(fallbackKey, fullReport);
          }
          parsed = fullReport ? u3125ExtractCustomerDetailFromReport(fullReport, trimmedCustomerId, bucket) : null;
        }
        if (parsed) {
          detailCache.set(cacheKey, parsed);
        }
        if (lastRequestKeyRef.current === requestKey) {
          setCustomerDetail(parsed);
        }
        return parsed;
      } catch (error: unknown) {
        if (lastRequestKeyRef.current === requestKey) {
          setCustomerDetail(null);
        }
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return null;
      } finally {
        if (lastRequestKeyRef.current === requestKey) {
          setLoadingCustomerId(null);
        }
      }
    },
    [axios, i18n.language, t, toast],
  );

  const clearCustomerDetail = useCallback(() => {
    setCustomerDetail(null);
    setLoadingCustomerId(null);
    lastRequestKeyRef.current = "";
  }, []);

  return {
    customerDetail,
    loadingCustomerId,
    loadCustomerDetail,
    clearCustomerDetail,
    setCustomerDetail,
  };
}
